/**
 * 显示层凭据脱敏 — `credential-in-message` 的兜底（授权来源：产品负责人 seq=86 §六.2，定级 P1）。
 *
 * 暴露面只有一处：`ApiError.displayMessage` 会把后端 `message` **原样**拼进 `ElMessage`（未知业务码走
 * 默认支）。若后端把口令/token 回显在 `message` 里（"旧口令 123456 不正确"这类），它就会落在屏幕上、
 * 并被截图带进台账与验收材料。⇒ 在这一处出口做**只改文案**的兜底。
 *
 * 三条边界，一条比一条容易踩：
 * 1. **⛔ 动判定**：分支用的 `this.message`／`code`／`resultCode` 一律不碰——脱敏后的串只活在屏幕上。
 *    一旦拿它去比业务码或错误文案，就把"改了显示"变成了"改了语义"。
 * 2. **⛔ 动日志**：`console.error` 那条幂等日志（以及契约漂移证据）保留原文。日志是唯一能回放现场的东西，
 *    把它抹干净等于让后端拿不到复现串——泄漏面本来就是"给人看的那一格"。
 * 3. **⛔ 把泄漏读成静默**：命中即计数并照常弹出（换成 `●●●` 而不是不弹）。静默会让用户以为操作没发生，
 *    比看到"旧口令 ●●● 不正确"更糟，也会让这条计数永远停在 0。
 *
 * 账本刻意**只留 `code`＋规则名＋时刻**（⛔ 落原文／脱敏后文案）——把疑似凭据抄进另一张内存表，
 * 等于换个地方继续泄漏。逐时报备要的是"今轮命中几枚、按哪条规则"，不是内容本身。
 */

/** 一条规则命中后的结果：⛔ 携带原文（连脱敏后文案都不留，只留"改过没有、按哪条改的"）。 */
export interface SanitizeResult {
  text: string;
  /** 命中的规则名；null＝未命中（原样返回） */
  rule: string | null;
}

const MASK = '●●●';

/**
 * 标签驱动。⚠ 「含凭据字段名」远不等于「回显了凭据」——「密码为何不合法」「请重新输入密码」「口令强度不足」
 * 都是要原样呈现的正常提示，掩掉就把错误原因糊成一团。⇒ 三条臂都靠**值的形状**收口：
 * · 引号内整体（含中文也掩——引号本身就是"这是一个值"的强信号）；
 * · 标签＋分隔符＋**可见 ASCII 连续段**（`[\x21-\x7e]`，≥4 字符）：口令／token 的现实形状；
 *   中文句子里「为」「是」后面跟的是 CJK，被这一条闸挡掉，⛔ 靠加停用词表去救（表永远缺一个词）。
 * · `Bearer <token>` 无标签也掩（鉴权头的固定形）。
 * 残余风险（刻意的漏，不是笔误）：`旧口令为 我的猫叫咪咪` 这类**中文裸值**不掩——宁可漏掩一个
 * 非 ASCII 口令，也不批量掩坏真实错误原因。命中面靠 `credential-in-message` 计数盯，⛔ 读成"已全覆盖"。
 */
const LABEL = '(?:旧|新|确认|当前|原始)?\\s*(?:口令|密码|动态码|验证码|passw(?:or)?d|passwd|pwd|secret|api[_-]?key|access[_-]?token|refresh[_-]?token|token)';
const SEP = '\\s*(?:[:：=]|为|是)\\s*';
const ASCII_VALUE = '[\\x21-\\x7e]{4,200}';

const RULES: Array<{ name: string; re: RegExp; mask: (m: string, ...g: string[]) => string }> = [
  {
    // 「口令为 "abc"」／「password: 'abc'」——引号内整段换掉，引号与标签保留（用户还得知道是哪一个字段出错）
    name: 'quoted-credential',
    re: new RegExp(`(${LABEL}${SEP}?)(["'“‘])([^"'”’]{1,200})\\2`, 'gi'),
    mask: (_all, label, q) => `${label}${q}${MASK}${q}`,
  },
  {
    // 「口令:abc123」「token=eyJ...」「新密码为 P@ss2024」——掩值、留标签与分隔符
    name: 'labeled-credential',
    re: new RegExp(`(${LABEL}${SEP})(${ASCII_VALUE})`, 'gi'),
    mask: (_all, label, _v) => `${label}${MASK}`,
  },
  {
    // 「旧口令 123456 不正确」——无分隔符，只靠空格分词；值必须整段 ASCII 才认
    name: 'spaced-credential',
    re: new RegExp(`(${LABEL})\\s+(${ASCII_VALUE})`, 'gi'),
    mask: (_all, label, _v) => `${label} ${MASK}`,
  },
  {
    name: 'bearer-token',
    re: /(Bearer\s+)[A-Za-z0-9._~+/=-]{8,}/gi,
    mask: (_all, prefix) => `${prefix}${MASK}`,
  },
];

/**
 * 纯函数：一次显示文案的脱敏。多条规则可连中（"旧口令为 abc，新口令: def"）⇒ 逐条套用后返回首个命中的
 * 规则名做计数标签，⛔ 声称"全量识别"：本函数证的是"已知形状不回显"，不是"这条 message 里没有凭据"。
 */
export function sanitizeDisplayMessage(message: string): SanitizeResult {
  if (!message) return { text: message, rule: null };
  let text = message;
  let rule: string | null = null;
  for (const { name, re, mask } of RULES) {
    const next = text.replace(re, (...args: string[]) => mask(args[0], args[1], args[2]));
    if (next !== text) {
      text = next;
      rule = rule ?? name;
    }
  }
  return { text, rule };
}

export interface CredentialMessageHit {
  /** 业务码（`resultCode` 优先的口径下由调用方传入），定位是哪一枚接口在回显 */
  code: string | number | null;
  rule: string;
  at: string;
}

const hits = new Map<string, CredentialMessageHit & { count: number }>();

export function getCredentialMessageHits(): Array<CredentialMessageHit & { count: number }> {
  return Array.from(hits.values());
}

export function clearCredentialMessageHits(): void {
  hits.clear();
}

/** 同一枚（code, rule）只占一格并累计——逐时报备要"命中几类、各几枚"，⛔ 被一次刷屏撑成几十行。 */
export function recordCredentialMessageHit(code: string | number | null, rule: string): void {
  const key = `${String(code ?? '-')}|${rule}`;
  const prev = hits.get(key);
  if (prev) {
    prev.count += 1;
    prev.at = new Date().toISOString();
    return;
  }
  hits.set(key, { code: code ?? null, rule, at: new Date().toISOString(), count: 1 });
  console.warn(`[credential-in-message] 业务码 ${String(code ?? '-')} 的应答文案疑似回显凭据（规则 ${rule}），已在显示层脱敏；判定与日志未改。`);
}
